import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { LoanFormat } from '../../../models/LoanFormat';
import { AgeCategory } from '../../../models/AgeCategory';
import { GlobalReactQueryOptions } from '../../config';
import {
    fetchLanguageLearningLanguages,
    fetchLanguagesForEntryPage,
    FetchLanguagesResponse,
    fetchLoteLanguages
} from '../../api/languages/fetchLanguages';
import { UseQueryResult } from '../../api/query-types';
import { useTranslation } from 'react-i18next';
import { ALL_LANGUAGES } from './constants';

export enum LanguageScope {
    LANGUAGE_LEARNING = 'languageLearning',
    LOTE = 'lote',
    LANGUAGES = 'languages'
}

const fetchFuncMapping = {
    languageLearning: fetchLanguageLearningLanguages,
    lote: fetchLoteLanguages,
    languages: fetchLanguagesForEntryPage
};

export type Language = {
    readonly key: string;
    readonly value: string;
    readonly count: number;
};

type Options = {
    readonly scope: keyof typeof fetchFuncMapping;
    readonly loanFormat: LoanFormat | undefined;
    readonly ageCategory: AgeCategory;
    readonly shopLanguage: string | undefined;
};

type UseLanguageLearningLanguages = UseQueryResult<'languages', ReadonlyArray<Language>>;

const initialData: FetchLanguagesResponse = {
    buckets: []
};

export function useLanguages({ scope, loanFormat, ageCategory, shopLanguage }: Options): UseLanguageLearningLanguages {
    const params = { loanFormat, ageCategory, shopLanguage };
    const { data, ...result } = useQuery(['languages', scope, params], () => fetchFuncMapping[scope](params), {
        ...GlobalReactQueryOptions,
        initialData
    });
    const { t } = useTranslation();
    const languages = useMemo(
        () =>
            data.buckets
                .map(bucketEntry => ({
                    key: bucketEntry.key,
                    value:
                        bucketEntry.key === ALL_LANGUAGES
                            ? t('allLanguages')
                            : scope === 'languageLearning'
                            ? // language learning languages are translated in the backend (via genre id)
                              bucketEntry.value ?? bucketEntry.key
                            : t(`filters:language.${bucketEntry.key}`, bucketEntry.value ?? bucketEntry.key),
                    count: bucketEntry.docCount
                }))
                .sort(sortLanguageBuckets),
        [data.buckets, scope, t]
    );

    return {
        ...result,
        languages
    };
}

export const sortLanguageBuckets = (a: { key: string; value: string }, b: { key: string; value: string }) => {
    const aFirstLevel = a.key === ALL_LANGUAGES ? 0 : 1;
    const bFirstLevel = b.key === ALL_LANGUAGES ? 0 : 1;

    if (aFirstLevel - bFirstLevel === 0) {
        return a.value.localeCompare(b.value);
    }

    return aFirstLevel - bFirstLevel;
};
